import type { Mode } from "../content/modes";
import type { SaveState } from "../save/schema";
import { cardStripes } from "./stripes";
import { trialFor, type TrialSpec } from "./trials";

/** How many recent tries the card's chart shows. */
export const RECENT_TRIES = 10;

export interface CardStats {
  /** The trial that earns this card in this strand, or null for Seedling. */
  spec: TrialSpec | null;
  held: boolean;
  best: number;
  tries: number;
  /** ISO date the trial was first passed. */
  passedOn: string | null;
  /** Right answers still needed to pass, or null once held. */
  toGo: number | null;
  /** Stripes on the trial for this card (for the next card, when this is the current one). */
  stripes: number;
  /** Recent scores on this card's trial, oldest first. History is capped, so this may be short. */
  recent: { score: number; passed: boolean }[];
}

/** The side-panel record of one card in one strand. Pure. */
export function cardStats(save: SaveState, mode: Mode, rankIndex: number): CardStats {
  const spec = trialFor(mode, rankIndex);
  const rec = save.trials[mode]?.[String(rankIndex)];
  const held = save.rank[mode] >= rankIndex;
  const best = rec?.best ?? 0;
  const recent = save.history
    .filter((h) => h.kind === "trial" && h.mode === mode && h.rankIndex === rankIndex)
    .slice(0, RECENT_TRIES)
    .reverse()
    .map((h) => ({ score: h.score, passed: !!h.passed }));
  return {
    spec,
    held,
    best,
    tries: rec?.attempts ?? 0,
    passedOn: rec?.passed ?? null,
    toGo: held || !spec ? null : Math.max(0, spec.passMark - best),
    stripes:
      rankIndex === save.rank[mode]
        ? cardStripes(save.stripes, mode, rankIndex)
        : (save.stripes[mode]?.[String(rankIndex)] ?? 0),
    recent,
  };
}
